import React, { ReactElement, RefObject } from 'react'

import {
  ColumnConfig,
  ColumnPinning,
  GroupByCellRenderer,
  GroupByRenderer,
} from '../../DataGrid.types'
import { gridClassNames } from '../../utils'
import { getColumnPinning, getLeftRightVars } from '../../utils/columnPinning'
import { StickyController } from '../../utils/StickyController'
import * as S from './RowsGroupHeaderRow.styles'

interface RowsGroupHeaderRowProps<RowType> {
  columns: ColumnConfig<RowType>[]
  rowData: RowType
  groupByField: keyof RowType
  groundByRenderer?: GroupByRenderer
  groupByCellRenderer?: GroupByCellRenderer
  pinnedColumns: number
  pinnedColumnsRight: number
  stickyControllerRef: RefObject<StickyController>
}
export function RowsGroupHeaderRow<RowType>({
  columns,
  groupByField,
  groundByRenderer,
  groupByCellRenderer,
  pinnedColumns,
  pinnedColumnsRight,
  rowData,
  stickyControllerRef,
}: RowsGroupHeaderRowProps<RowType>) {
  if (groupByCellRenderer) {
    // we render separate grid cells
    return columns.map((column, columnIndex) => {
      const columnPinning = getColumnPinning(
        columnIndex,
        pinnedColumns,
        pinnedColumnsRight,
        columns,
      )

      return (
        <S.RowsGroupHeaderCell
          key={`rowGroup-${rowData[groupByField]}-${column.columnKey}`}
          className={`${gridClassNames.rowsGroupHeaderRow} ${gridClassNames.rowsGroupHeaderRow}-${column.columnKey} column-${columnIndex}`}
          $headerHeightVar={stickyControllerRef.current?.getHeaderHeightVar()}
          $isFirstColumn={columnIndex === 0}
          {...getLeftRightVars(columnPinning, stickyControllerRef, columnIndex)}
          $isPinnedColumn={columnPinning !== ColumnPinning.None}
          $isSticky
          $noRightBorder={columnIndex === columns.length - 1}
        >
          {groupByCellRenderer({
            groupRowsByFieldValue: rowData[groupByField] as string,
            columnIndex,
            columnKey: column.columnKey,
          })}
        </S.RowsGroupHeaderCell>
      )
    })
  }

  // we render one cell spanning all columns
  return (
    <S.RowsGroupHeaderRow
      className={gridClassNames.rowsGroupHeaderRowCell}
      key={`rowGroup-${rowData[groupByField]}`}
      $headerHeightVar={stickyControllerRef.current?.getHeaderHeightVar()}
      $noRightBorder
    >
      <S.RowsGroupHeaderRowContent className={gridClassNames.rowsGroupHeaderRowContent}>
        {groundByRenderer
          ? groundByRenderer(rowData[groupByField] as string)
          : (rowData[groupByField] as ReactElement)}
      </S.RowsGroupHeaderRowContent>
    </S.RowsGroupHeaderRow>
  )
}
